/**
 * G014 — 应用外壳：主菜单、暂停菜单、退出闭环。
 *
 * 在此之前游戏只有「打开就在玩」这一个状态：没有入口、没有暂停菜单、没有退出，
 * 关掉标签页殖民地就没了。本模块补上会话的完整生命周期：
 *
 *   主菜单 ──新游戏/继续/读档──▶ 游戏中 ──Esc──▶ 暂停菜单
 *      ▲                                            │
 *      └────────────── 返回主菜单（先问存不存） ◀────┘
 *
 * 与 main.ts 的边界：本模块**不碰任何领域状态**，只调 kernel-bridge 的会话
 * 接口与 save-store，并通过回调请求渲染层清场/重建。
 */
import * as K from './kernel-bridge';
import {
  writeSlot, readSlot, listSlots, latestSlot, hasAnySave,
  MANUAL_SLOTS, type SlotId, type SlotInfo,
} from './save-store';

export type ShellPhase = 'menu' | 'playing' | 'paused';

/** 渲染层需要提供的钩子。外壳只发指令，不直接操作 Three.js。 */
export interface ShellHost {
  /** 清空场景里的全部殖民地内容（建筑、道路、电缆、管道）。 */
  teardownScene(): void;
  /** 按当前内核状态重建场景。newGame=true 时还要播种 demo 城。 */
  rebuildScene(opts: { seedDemo: boolean }): void;
  /** 暂停/恢复模拟推进。 */
  setPaused(paused: boolean): void;
  /** 取当前摄像机元数据（存档要存，但不进权威状态）。 */
  captureView(): K.ViewMetaLike;
  /** 恢复摄像机。 */
  restoreView(v: K.ViewMetaLike | null | undefined): void;
  toast(msg: string): void;
}

const SAVE_REJECT: Record<string, string> = {
  SAVE_NOT_OBJECT: '文件不是有效存档',
  SAVE_WRONG_PRODUCT: '这不是《零号陨坑》的存档',
  SAVE_WRONG_NAMESPACE: '存档命名空间不匹配',
  SAVE_VERSION_UNSUPPORTED: '存档版本不受支持（可能来自更新的版本）',
  SAVE_CHECKSUM_MISMATCH: '存档已损坏（校验和不匹配）',
  SAVE_WORLD_MALFORMED: '存档内容残缺，可能是写入中断',
};

let host: ShellHost;
let phase: ShellPhase = 'menu';
let dirty = false;                 // 自上次存档后是否有变化
let autoTimer: number | null = null;

export function currentPhase(): ShellPhase { return phase; }
/** 标记「有未保存的进度」。放置/拆除等操作后由 main.ts 调用。 */
export function markDirty(): void { dirty = true; }

/* ===================== DOM ===================== */

function el(id: string): HTMLElement | null { return document.getElementById(id); }
function show(id: string, on: boolean): void {
  const e = el(id);
  if (e) e.style.display = on ? 'flex' : 'none';
}

function fmtSlot(info: SlotInfo | null): string {
  if (!info) return '<span class="slot-empty">空槽位</span>';
  const hh = String(Math.floor(info.minuteOfDay / 60)).padStart(2, '0');
  const mm = String(info.minuteOfDay % 60).padStart(2, '0');
  const when = info.createdAtMs
    ? new Date(info.createdAtMs).toLocaleString('zh-CN', { hour12: false })
    : '—';
  return `<span class="slot-main">第 ${info.day} 天 ${hh}:${mm} · ${info.buildings} 座建筑</span>`
    + `<span class="slot-sub">${when}</span>`;
}

async function renderSlots(containerId: string, action: 'save' | 'load'): Promise<void> {
  const box = el(containerId);
  if (!box) return;
  const slots = await listSlots();
  const rows = slots.map((info, i) => {
    const slot = (['auto', ...MANUAL_SLOTS] as SlotId[])[i]!;
    const isAuto = slot === 'auto';
    // 自动存档不允许手动覆盖：它是崩溃兜底，被玩家写脏就失去了意义
    const disabled = action === 'save' && isAuto;
    const empty = info === null;
    const clickable = action === 'save' ? !disabled : !empty;
    return `<button class="slot" data-slot="${slot}" data-action="${action}"
      ${clickable ? '' : 'disabled'}>
      <span class="slot-name">${isAuto ? '自动存档' : `槽位 ${i}`}</span>
      <span class="slot-body">${fmtSlot(info)}</span>
    </button>`;
  }).join('');
  box.innerHTML = rows;
  box.querySelectorAll<HTMLElement>('.slot').forEach((b) => {
    b.onclick = (): void => {
      const slot = b.dataset['slot'] as SlotId;
      if (b.dataset['action'] === 'save') void doSave(slot);
      else void doLoad(slot);
    };
  });
}

/* ===================== 动作 ===================== */

async function doSave(slot: SlotId): Promise<void> {
  try {
    const env = K.makeSave(Date.now(), host.captureView());
    await writeSlot(slot, env);
    dirty = false;
    host.toast(`💾 已保存到${slot === 'auto' ? '自动存档' : `槽位 ${slot.slice(-1)}`}`);
    await renderSlots('shSaveSlots', 'save');
  } catch (e) {
    // 安全提交保证旧档还在，因此这里只报错不做任何回滚
    host.toast(`⛔ 保存失败：${e instanceof Error ? e.message : '未知错误'}（旧存档已保留）`);
  }
}

async function doLoad(slot: SlotId): Promise<void> {
  let raw: unknown;
  try {
    raw = await readSlot(slot);
  } catch {
    host.toast('⛔ 读取存档失败');
    return;
  }
  if (!raw) { host.toast('⚠️ 该槽位是空的'); return; }

  const res = K.applySave(raw);
  if (!res.ok) {
    host.toast(`⛔ ${SAVE_REJECT[res.code ?? ''] ?? '存档无法读取'}`);
    return;
  }
  // 先清场再重建 —— 渲染层的建筑网格与内核实体是一一对应的，不清会残留
  host.teardownScene();
  host.rebuildScene({ seedDemo: false });
  host.restoreView(res.view);
  dirty = false;
  enterPlaying();
  host.toast('📂 存档已载入');
}

function startNew(seedDemo: boolean): void {
  K.newSession();
  host.teardownScene();
  host.rebuildScene({ seedDemo });
  dirty = seedDemo;
  enterPlaying();
  host.toast(seedDemo ? '🏙️ 已进入示范殖民地' : '🏗️ 从建造菜单选择建筑开始');
}

async function continueLatest(): Promise<void> {
  const slot = await latestSlot();
  if (!slot) { host.toast('⚠️ 没有可继续的存档'); return; }
  await doLoad(slot);
}

/** 导出当前世界为 JSON 文件，供玩家备份或提交问题报告。 */
function exportJson(): void {
  const env = K.makeSave(Date.now(), host.captureView());
  const blob = new Blob([JSON.stringify(env, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `crater_zero_d${env.label.day}_${env.label.buildings}b.json`;
  a.click();
  URL.revokeObjectURL(url);          // 立刻回收，否则 blob 会一直留在内存里
  host.toast('⭳ 存档已导出');
}

/** 从文件导入存档。走与槽位读取完全相同的校验路径 —— 外部文件更需要校验。 */
function importJson(file: File): void {
  const reader = new FileReader();
  reader.onload = (): void => {
    let raw: unknown;
    try {
      raw = JSON.parse(String(reader.result));
    } catch {
      host.toast('⛔ 文件不是有效的 JSON');
      return;
    }
    const res = K.applySave(raw);
    if (!res.ok) {
      host.toast(`⛔ ${SAVE_REJECT[res.code ?? ''] ?? '存档无法读取'}`);
      return;
    }
    host.teardownScene();
    host.rebuildScene({ seedDemo: false });
    host.restoreView(res.view);
    dirty = false;
    enterPlaying();
    host.toast('📂 已从文件载入');
  };
  reader.onerror = (): void => { host.toast('⛔ 读取文件失败'); };
  reader.readAsText(file);
}

/* ===================== 阶段切换 ===================== */

function enterPlaying(): void {
  phase = 'playing';
  /* 必须隐藏**全部**遮罩。原先只关 shMenu 与 shPause，走「难度 → 简介 → 开始」
     这条路进游戏时，场景简介会一直盖在画面上 —— 玩家点了开始却动不了。
     同时把屏幕栈重置为主菜单：进游戏后再进设置，返回应回主菜单，
     而不是退回上一次开局时的简介页。 */
  hideAllScreens();
  screenStack = ['shMenu'];
  host.setPaused(false);
  startAutosave();
}

export function openPause(): void {
  if (phase !== 'playing') return;
  phase = 'paused';
  host.setPaused(true);
  show('shPause', true);
  // 把本局难度显示出来 —— 记录了却看不见，等于没记录
  const d = el('shPauseDiff');
  if (d) d.textContent = `本局难度：${DIFF_LABEL[selectedDiff]}（暂不影响模拟）`;
  void renderSlots('shSaveSlots', 'save');
}

export function closePause(): void {
  if (phase !== 'paused') return;
  phase = 'playing';
  show('shPause', false);
  host.setPaused(false);
}

async function toMenu(): Promise<void> {
  if (dirty && !confirm('有未保存的进度，确定返回主菜单吗？')) return;
  stopAutosave();
  phase = 'menu';
  show('shPause', false);
  host.setPaused(true);
  host.teardownScene();
  show('shMenu', true);
  await refreshMenu();
}

/**
 * 退出游戏。
 *
 * 浏览器里**无法真正关闭标签页**（`window.close()` 只对脚本自己打开的窗口有效），
 * 所以这里如实告知而不是假装退出。桌面版（Tauri）会走真正的退出接口。
 */
async function quit(): Promise<void> {
  if (dirty && !confirm('有未保存的进度，确定退出吗？')) return;
  stopAutosave();
  const tauri = (window as unknown as { __TAURI__?: { process?: { exit(code: number): void } } }).__TAURI__;
  if (tauri?.process) { tauri.process.exit(0); return; }
  await toMenu();
  host.toast('浏览器中无法自动关闭标签页，请手动关闭窗口');
}

/* ===================== 自动存档 ===================== */

const AUTOSAVE_MS = 120_000;

function startAutosave(): void {
  stopAutosave();
  autoTimer = window.setInterval(() => {
    if (phase !== 'playing' || !dirty) return;
    void doSave('auto');
  }, AUTOSAVE_MS);
}
function stopAutosave(): void {
  if (autoTimer !== null) { clearInterval(autoTimer); autoTimer = null; }
}

/* ===================== 屏幕流转扩展 ===================== */

type ScreenId = 'shBoot' | 'shMenu' | 'shDiff' | 'shIntro' | 'shSettings' | 'shPause' | 'shEnd';
/** 全部全屏遮罩。进入游戏时必须**逐一**隐藏 —— 只关其中两个会把别的留在画面上。 */
const ALL_SCREENS: readonly ScreenId[] = [
  'shBoot', 'shMenu', 'shDiff', 'shIntro', 'shSettings', 'shPause', 'shEnd',
];
function hideAllScreens(): void { for (const s of ALL_SCREENS) show(s, false); }
let screenStack: ScreenId[] = [];
export type Difficulty = 'story' | 'standard' | 'commander';
let selectedDiff: Difficulty = 'standard';
/** 本局难度。当前只被记录与展示，**尚未影响任何模拟参数**（见 initShell 注释）。 */
export function sessionDifficulty(): Difficulty { return selectedDiff; }

function showScreen(id: ScreenId): void {
  for (const s of ALL_SCREENS) show(s, s === id);
  if (screenStack[screenStack.length - 1] !== id) screenStack.push(id);
}

function goBack(): void {
  screenStack.pop();
  const prev = screenStack[screenStack.length - 1];
  if (prev) showScreen(prev);
  else showScreen('shMenu');
}

function bindSettingsTabs(): void {
  document.querySelectorAll('.set-tab').forEach((tab) => {
    tab.addEventListener('click', () => {
      document.querySelectorAll('.set-tab').forEach((t) => t.classList.remove('on'));
      tab.classList.add('on');
      const target = (tab as HTMLElement).dataset['tab'];
      document.querySelectorAll('.set-pane').forEach((p) => {
        (p as HTMLElement).style.display = p.id === `set${target?.charAt(0).toUpperCase()}${target?.slice(1)}` ? 'flex' : 'none';
      });
    });
  });
}

const DIFF_LABEL: Record<Difficulty, string> = {
  story: '故事', standard: '标准', commander: '指挥官',
};

function bindDiffCards(): void {
  document.querySelectorAll('.diff-card').forEach((card) => {
    card.addEventListener('click', () => {
      document.querySelectorAll('.diff-card').forEach((c) => c.classList.remove('sel'));
      card.classList.add('sel');
      selectedDiff = (card as HTMLElement).dataset['diff'] as typeof selectedDiff;
    });
  });
}

/* ===================== 初始化 ===================== */

async function refreshMenu(): Promise<void> {
  const cont = el('shContinue') as HTMLButtonElement | null;
  if (cont) {
    const any = await hasAnySave();
    cont.disabled = !any;
    cont.title = any ? '' : '还没有任何存档';
  }
  await renderSlots('shLoadSlots', 'load');
}

export function initShell(h: ShellHost, opts: { skipMenu: boolean; seedDemo: boolean }): void {
  host = h;

  /* ---- 启动画面：任意键/点击进入主菜单 ---- */
  const boot = el('shBoot');
  const dismissBoot = (): void => {
    if (!boot || boot.style.display === 'none') return;
    boot.style.display = 'none';
    screenStack = ['shMenu'];
    showScreen('shMenu');
    void refreshMenu();
  };
  boot?.addEventListener('click', dismissBoot);
  window.addEventListener('keydown', function onBootKey(e): void {
    // 无论是键盘还是点击先解掉启动画面，这个监听器都要摘掉；
    // 原实现只在「由它自己解掉」时移除，点击进入后它会一直挂着。
    if (boot && boot.style.display !== 'none') { dismissBoot(); e.preventDefault(); }
    window.removeEventListener('keydown', onBootKey);
  });

  /* ---- 主菜单 ---- */
  el('shNewFresh')?.addEventListener('click', () => { showScreen('shDiff'); });
  el('shNewDemo')?.addEventListener('click', () => { startNew(true); });
  el('shContinue')?.addEventListener('click', () => { void continueLatest(); });
  el('shSettingsBtn')?.addEventListener('click', () => { showScreen('shSettings'); });
  el('shQuitMenu')?.addEventListener('click', () => { void quit(); });

  /* ---- 难度选择 ---- */
  bindDiffCards();
  el('shDiffBack')?.addEventListener('click', () => { goBack(); });
  el('shDiffNext')?.addEventListener('click', () => { showScreen('shIntro'); });

  /* ---- 场景简介 ---- */
  el('shIntroBack')?.addEventListener('click', () => { goBack(); });
  el('shIntroStart')?.addEventListener('click', () => {
    /* 难度当前**只被记录，不改变任何模拟参数**。
       按产品宪章，三档难度的差异在于预警置信度、灾害压力与资源/维护紧度 ——
       这些系统属于 G013（需求健康）与 G018（灾害），尚未实现。
       与其让玩家以为选了个有效果的东西，不如把它显式标注出来：
       难度页有「暂不影响模拟」的提示，暂停菜单显示当前档位，选择本身被保留。 */
    startNew(false);
    host.toast(`已选择难度：${DIFF_LABEL[selectedDiff]}（暂不影响模拟，将随灾害系统生效）`);
  });

  /* ---- 设置 ---- */
  bindSettingsTabs();
  el('shSetBack')?.addEventListener('click', () => { goBack(); });
  el('shSetSave')?.addEventListener('click', () => { goBack(); });

  /* ---- 失败结算（G013 预留） ---- */
  el('shEndRetry')?.addEventListener('click', () => { showScreen('shDiff'); });
  el('shEndMenu')?.addEventListener('click', () => { void toMenu(); });

  /* ---- 暂停菜单 ---- */
  el('shResume')?.addEventListener('click', () => { closePause(); });
  el('shExport')?.addEventListener('click', () => { exportJson(); });
  el('shImport')?.addEventListener('click', () => { (el('shImportFile') as HTMLInputElement | null)?.click(); });
  el('shImportFile')?.addEventListener('change', (e) => {
    const f = (e.target as HTMLInputElement).files?.[0];
    if (f) importJson(f);
    (e.target as HTMLInputElement).value = '';   // 允许重复选同一个文件
  });
  el('shToMenu')?.addEventListener('click', () => { void toMenu(); });
  el('shQuit')?.addEventListener('click', () => { void quit(); });

  /* ---- Esc：游戏中打开暂停菜单，暂停中关闭 ---- */
  window.addEventListener('keydown', (e) => {
    if (e.code !== 'Escape') return;
    if (window.__placingMode) return;                 // 建造模式的 Esc 归它自己
    if (phase === 'playing') { openPause(); e.preventDefault(); }
    else if (phase === 'paused') { closePause(); e.preventDefault(); }
  });

  /* ---- 关标签页前提醒 ---- */
  window.addEventListener('beforeunload', (e) => {
    if (phase !== 'menu' && dirty) { e.preventDefault(); e.returnValue = ''; }
  });

  /* ---- 初始状态 ---- */
  if (opts.skipMenu) {
    startNew(opts.seedDemo);
  } else {
    phase = 'menu';
    screenStack = ['shBoot'];
    showScreen('shBoot');
    host.setPaused(true);
  }
}
